import { API_BASE_URL, X_BLOCKS_KEY } from "./config"
import { useAuthStore } from "@/state/store/auth"

export type ResponseType = 'json' | 'text' | 'blob' | 'arrayBuffer';

export interface FetchOptions<TBody = unknown> extends Omit<RequestInit, 'body'> {
    body?: TBody;
    responseType?: ResponseType;
}

/**
 * Single Responsibility: Builds and formats the fetch Request options.
 */
class RequestBuilder {
    static build<TBody>(options?: FetchOptions<TBody>): RequestInit {
        const { body, ...restOptions } = options || {};

        const fetchOptions: RequestInit = {
            ...restOptions,
            credentials: restOptions.credentials || 'include',
            headers: {
                "x-blocks-key": X_BLOCKS_KEY,
                ...restOptions.headers,
            },
        };
        delete (fetchOptions as any).responseType;

        if (body !== undefined) {
            this.serializeBody(body, fetchOptions);
        }

        return fetchOptions;
    }

    private static serializeBody(body: unknown, options: RequestInit): void {
        if (body instanceof FormData || body instanceof Blob) {
            options.body = body;
        } else if (typeof body === 'object' && body !== null) {
            options.headers = {
                "Content-Type": "application/json",
                ...options.headers,
            };
            options.body = JSON.stringify(body);
        } else {
            options.body = String(body);
        }
    }
}

/**
 * Single Responsibility: Parses the fetch Response based on content-type or explicit overrides.
 */
class ResponseParser {
    static async parse<TResponse>(response: Response, explicitType?: ResponseType): Promise<TResponse> {
        if (!response.ok) {
            const contentType = response.headers.get("content-type") || "";
            let errorData: unknown = null;
            if (contentType.includes("application/json")) {
                errorData = await response.clone().json().catch(() => null);
                if ((errorData as { error?: string } | null)?.error === "invalid_refresh_token") {
                    useAuthStore.getState().expireSession();
                    window.location.href = '/login';
                }
            }
            const error = new Error(`HTTP error! status: ${response.status}`) as Error & {
                status: number;
                data: unknown;
            };
            error.status = response.status;
            error.data = errorData;
            throw error;
        }

        const contentType = response.headers.get("content-type") || "";

        if (explicitType === 'blob' || this.isBlobContent(contentType)) {
            return (await response.blob()) as TResponse;
        }

        if (explicitType === 'text' || contentType.includes('text/')) {
            return (await response.text()) as TResponse;
        }

        if (explicitType === 'arrayBuffer') {
            return (await response.arrayBuffer()) as TResponse;
        }

        return (await response.json()) as TResponse;
    }

    private static isBlobContent(contentType: string): boolean {
        return contentType.includes('image/') ||
            contentType.includes('application/pdf') ||
            contentType.includes('application/octet-stream');
    }
}

/**
 * Single Responsibility: Manages authentication state, specifically token refreshing.
 */
class AuthManager {
    private static refreshTokenPromise: Promise<boolean> | null = null;

    static async handleUnauthorized<TResponse>(
        retryRequest: () => Promise<TResponse>
    ): Promise<TResponse> {
        if (!this.refreshTokenPromise) {
            this.refreshTokenPromise = this.refreshAccessToken();
        }

        const success = await this.refreshTokenPromise;
        this.refreshTokenPromise = null;

        if (!success) {
            throw new Error('Unauthorized - Token refresh failed');
        }

        return retryRequest();
    }

    private static async refreshAccessToken(): Promise<boolean> {
        try {
            const response = await fetch(`${API_BASE_URL}/iam/v4/auth/refresh`, {
                method: 'POST',
                credentials: 'include',

                headers: { "x-blocks-key": X_BLOCKS_KEY, "Content-Type": "application/json", },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                const contentType = response.headers.get("content-type") || "";
                if (contentType.includes("application/json")) {
                    const errorData = await response.clone().json().catch(() => null);
                    if (errorData?.error === "invalid_refresh_token") {
                        useAuthStore.getState().expireSession();
                        window.location.href = '/login';
                    }
                }
            }

            return response.ok;
        } catch {
            return false;
        }
    }
}

/**
 * Facade: Orchestrates the API requests by combining the Builder, AuthManager, and Parser.
 */
class ApiClient {
    static async fetch<TResponse = unknown, TBody = unknown>(
        endPoint: string,
        options?: FetchOptions<TBody>,
        customBaseUrl = false
    ): Promise<TResponse> {
        const url = customBaseUrl ? endPoint : `${API_BASE_URL}${endPoint}`;
        const fetchOptions = RequestBuilder.build(options);

        const executeRequest = async () => fetch(url, fetchOptions);

        const response = await executeRequest();

        if (response.status === 401) {
            return AuthManager.handleUnauthorized(async () => {
                const retryResponse = await executeRequest();
                return ResponseParser.parse<TResponse>(retryResponse, options?.responseType);
            });
        }

        return ResponseParser.parse<TResponse>(response, options?.responseType);
    }
}

const fetchData = <TResponse = unknown, TBody = unknown>(
    endPoint: string,
    options?: FetchOptions<TBody>,
    customBaseUrl = false
): Promise<TResponse> => ApiClient.fetch<TResponse, TBody>(endPoint, options, customBaseUrl);

export default fetchData;